import { creditCostFor, type CreditPricing } from '@/store/slices/aiCredits';

import { adLanguageBase } from './ad-language';
import {
  adApiRequest,
  normaliseAdSubject,
  normaliseVersionArtStyle,
  type AdApiResult,
  type AdSubject,
  type AdSubjectType,
  type AdVersionArtStyle,
} from './banner-kit-api';
import { LANDING_IMMERSIVE_SEEDS, LANDING_SEED_COPY } from './landing-seed-copy';
import type { PlayableTemplate } from './playable-api';
import type { StudioColours } from './studio-preview-colours';

/**
 * Landing Pages: the brand's live landing pages on its own `lp.` domain, its marketing offers and website footer legal
 * text, the premium template catalogue (the regular offer page and the full-screen games: a game is a template of its
 * own, never embedded in a page), and the landing page entity (slice 2): a spark-creatives `creatives` row
 * (`media_type='landing_page'`) with the generic `ad_params` / `ad_versions` columns like a mini-game, whose versions
 * Lumio fills (the scene art or a hero, the copy per language, the drawn lettering of the hero lines and the button)
 * and lock, and which publishes to `https://lp.<domain>/<path>`. The wire's `params.mechanic` (a game embedded in a
 * page) is deprecated: never read or sent.
 * Wire types mirror spark-creatives `pkg/landingtpl/landingtpl.go` (snake_case); every read is normalised
 * defensively (a missing field becomes its empty value), so a partial answer never throws.
 */

/* ---------- wire types (pkg/landingtpl) ---------- */

/** A template's format: a scrolling offer page (a game, if any, plays in its stage) or a full-screen game scene
 *  (layered parallax art, the game as the centrepiece, a multi-step reveal and a win modal). */
export type LandingTemplateFormat = 'page' | 'immersive';

/** The native game of an immersive template. */
export type LandingGame = 'wheel' | 'pick';

/** One entry of the template catalogue. */
export interface LandingTemplate {
  id: string;
  name: string;
  description: string;
  /** A few words on the look, e.g. "dark luxe, gold foil". */
  mood: string;
  /** The aspect ratio the hero art is drawn for, e.g. "16:9". */
  hero_aspect: string;
  /** How many "how it works" steps the layout shows. */
  max_steps: number;
  /** 'page' when the catalogue does not say. */
  format: LandingTemplateFormat;
  /** The native game of an immersive template; undefined for a page template (or a game this build does not know). */
  game?: LandingGame;
  /** The game's own art keys an immersive template draws from the scene's `parts` (e.g. "wheel_frame"); empty for a
   *  page template. */
  scene_parts: string[];
  /** The template shows layered scene art (`params.scene`: the backgrounds and the characters, a game's own parts and
   *  symbols too): the scene op is offered only then. */
  scene: boolean;
  /** The template shows one key art image (`params.hero`): the hero op is offered only then. */
  hero: boolean;
  /** Retired template ids that now open this template: a page saved on one opens, renders and generates as this one
   *  (and is saved with this id on its next edit). */
  aliases: string[];
}

/** What the renderer knows about the brand (resolved server-side; the composer never sends it). Colours are #rrggbb,
 *  '' when unknown. */
export interface LandingBrand {
  name: string;
  domain: string;
  logo_url: string;
  favicon_url: string;
  button_fill: string;
  button_text: string;
  /** -1 when unknown. */
  button_radius_px: number;
  button_pill: boolean;
  primary: string;
  secondary: string;
  background: string;
  text: string;
  font_heading: string;
  font_body: string;
  /** The faces the renderer may load for the brand: the website's own fonts (with their sources) and the display face
   *  suggested for it. Empty until the service resolves them. */
  fonts: LandingFontFace[];
  /** The catalogue display face matched to the brand's logo lettering and art style; '' until resolved. */
  display_suggestion: string;
}

/** One `@font-face` source of a web font (the website's own font files). */
export interface LandingFontSrc {
  url: string;
  /** "400", "700", "100 900". */
  weight?: string;
  /** "normal" | "italic". */
  style?: string;
  /** "woff2", "woff", "truetype", "opentype". */
  format?: string;
}

/** One web font a page loads: a family with its stylesheet (a Google Fonts css2 URL) or its `@font-face` sources. */
export interface LandingFontFace {
  family: string;
  href?: string;
  faces?: LandingFontSrc[];
}

/** The page's two faces. An empty field is resolved by the service for the brand: `display` the face matched to the
 *  brand's logo and art style (it sets the offer figures, headings, ribbons and game labels), `body` the brand
 *  website's own body font (everything else). */
export interface LandingTypography {
  /** A catalogue family name ("Bungee"); '' = matched to the brand. */
  display: string;
  /** A family name; '' = the website's body font. */
  body: string;
}

/** One language's footer legal copy, as HTML. */
export interface LandingLegalText {
  licence_html: string;
  copyright_html: string;
  responsible_html: string;
}

/** A footer badge (18+, regulator seal, responsible-gambling logo). */
export interface LandingLegalImage {
  src: string;
  alt: string;
  href: string;
}

/** Where the footer legal text comes from: the site's own licence text, the website's default footer translation, or
 *  nothing. */
export type LandingLegalSource = 'site' | 'platform' | 'none';

/** The brand website's footer legal text, shown verbatim at the foot of the page. */
export interface LandingLegal {
  source: LandingLegalSource;
  /** Keyed by language code. */
  texts: Record<string, LandingLegalText>;
  images: LandingLegalImage[];
}

/** One language's text of the page. */
export interface LandingCopy {
  badge: string;
  eyebrow: string;
  headline: string;
  subline: string;
  cta: string;
  micro: string;
  steps_title: string;
  steps: string[];
  terms_title: string;
  /** The offer's terms, HTML (p / ul / li / b only). */
  terms_html: string;
  /** The offer's key conditions as one readable line, shown as text under every button (every template), e.g.
   *  "18+ · New players only · T&Cs apply". */
  key_terms: string;
  /* The regular page's own sections; other templates ignore them. Always present ('' / []). */
  /** The "why play here" row, four items. */
  usps: LandingUSP[];
  /** The heading over the game tiles, e.g. "Popular games". */
  games_title: string;
  /** The heading over the questions, e.g. "Questions". */
  faq_title: string;
  /** Four to six questions about the offer. */
  faq: LandingFAQ[];
  /** The line over the repeated call to action near the foot, e.g. "Your bonus is waiting". */
  closing: string;
  /* Immersive (full-screen game) copy; page templates ignore it. Always present ('' / []). */
  /** The call to play, e.g. "Spin the wheel and claim your bonus!". */
  hint: string;
  /** One line of small print under the stage. */
  disclaimer: string;
  /** The reveal sequence, one per play (1 to 3); the last is the headline offer. */
  prizes: LandingPrize[];
  /** The other wheel segment / card labels, e.g. "$500", "10 free spins". */
  decoys: string[];
  /** The win modal's heading and line. */
  modal_title: string;
  modal_text: string;
  /** The wheel's centre button label, e.g. "SPIN & WIN" (wheel only; '' elsewhere or before Lumio writes it). */
  hub: string;
}

/** One item of the regular page's "why play here" row, e.g. "Fast withdrawals" / "Cash out in minutes". */
export interface LandingUSP {
  title: string;
  text: string;
}

/** One question of the regular page's FAQ. */
export interface LandingFAQ {
  q: string;
  a: string;
}

/** One drawn lettering cut-out: a transparent image of a slot's text in the page's art style. */
export interface LandingLettering {
  url: string;
  /** The exact text it was drawn from: the page shows it only while the copy still reads this (else the live text). */
  text: string;
  /** The box of its opaque core (x, y, w, h), normalised 0..1 to the image; the page lays the slot out by it. */
  core: [number, number, number, number];
  w: number;
  h: number;
}

/** One game tile of the regular page: one of the brand's own games (every tile links to the button's link). */
export interface LandingGameTile {
  id: string;
  name: string;
  image_url: string;
}

/** One step of an immersive reveal: `big` is the figure ("300", "$30.000"), `small` the words ("free spins"). */
export interface LandingPrize {
  big: string;
  small: string;
}

/** The layered art of an immersive template (all transparent cut-outs except the backgrounds); every field may be
 *  empty (the template draws a brand-coloured fallback). Always fully present ('' / [] / {}). */
export interface LandingScene {
  /** 16:9, no text, the centre kept clear for the game. */
  background_desktop_url: string;
  /** 9:16, the same scene. */
  background_mobile_url: string;
  /** Up to two character cut-outs (the brand's mascots), left and right of the game. */
  characters: string[];
  /** Up to ten small floating cut-outs (coins, chips, gems...). */
  symbols: string[];
  /** The game's own art, keyed by the template's `scene_parts`. */
  parts: Record<string, string>;
  /** What the generator measured on a part, keyed like `parts` (a wheel frame's bulbs); passed through untouched. */
  part_meta?: Record<string, LandingPartMeta>;
}

/** One light of a wheel frame: its centre and radius, normalised to the part image (0..1 of its width / height; `r`
 *  of its width). */
export interface LandingBulb {
  x: number;
  y: number;
  r: number;
}

/** A scene part's measured geometry. */
export interface LandingPartMeta {
  bulbs?: LandingBulb[];
}

/** What one drawn scene key's checks recorded (`assets.scene_qa[key]`); passed through untouched. */
export interface LandingSceneKeyQA {
  status?: string;
  /** Why the key is not `passed` (the first check its kept render failed, e.g. `text`, `logo`, `cut_off`,
   *  `unavailable`); absent when passed and on keys drawn before reasons were recorded. Not shown to the owner. */
  reason?: string;
  /** Who a character key drew: the art style's mascot id, "game" (a featured game's own character) or "fictional"
   *  (absent: drawn before it was recorded). */
  mascot?: string;
  [field: string]: unknown;
}

/** The marketing offer the page promotes ('' moid: none). */
export interface LandingOffer {
  moid: string;
  name: string;
}

/** What a page promotes as its key thing: one promotion or one to four of the brand's games. */
export type LandingFeaturedType = 'promotion' | 'game';

/** The most games a page can feature. */
export const LANDING_FEATURED_GAMES_MAX = 4;

/** The key thing the page promotes (`params.featured`): the copy, the scene and the lettering lead with it, while the
 *  welcome offer (`params.subject`) stays the page's conversion offer (its button, its link, its offer copy). Absent:
 *  the page leads with the welcome offer itself. */
export interface LandingFeatured {
  type: LandingFeaturedType;
  /** A promotion: exactly one. Games: one to {@link LANDING_FEATURED_GAMES_MAX}. */
  items: AdSubject[];
}

/** The page's key art. `video_url` is optional and plays muted in a loop over the poster / image. */
export interface LandingHero {
  image_url: string;
  video_url: string;
  poster_url: string;
}

/** What the owner edits in the composer and what a saved page stores on each version (`landingtpl.Params` plus the
 *  promotion the page is about and the owner's art note for generation). */
export interface LandingParams {
  template_id: string;
  languages: string[];
  default_language: string;
  /** The language the owner picked for the page to open in ('' = the website's default). The page keeps every website
   *  language; while this is one of them it is `default_language`, listed first ({@link withSiteLanguages}). */
  opening_language: string;
  copy: Record<string, LandingCopy>;
  offer: LandingOffer;
  cta_url: string;
  /** The owner's own key art; empty = the version's generated hero (`assets.hero_url`). */
  hero: LandingHero;
  /** Image URLs shown as a trust row; optional. */
  payment_icons: string[];
  /** The page's browser title; '' = "<brand> · <badge>". */
  title: string;
  /** The layered art of a template that shows a scene (`template.scene`; the others ignore it); the scene op writes
   *  it. */
  scene: LandingScene;
  /** The welcome promotion: the page's conversion offer (its button link and offer copy). Always attached. */
  subject_type: AdSubjectType;
  subject: AdSubject;
  /** What the page promotes as its key thing (one promotion or up to four games); absent: the welcome offer leads. */
  featured?: LandingFeatured;
  /** The owner's free-text art note for the generated hero. */
  art_direction: string;
  /** The page's faces; empty fields are resolved by the service (never reaches the art). The composer no longer picks
   *  them (the faces are automatic); a stored choice is kept and still honoured. */
  typography: LandingTypography;
  /** Which of the art style's mascots a scene shows, in order (left, right), at most {@link LANDING_CAST_MAX};
   *  `['none']` ({@link LANDING_CAST_NONE}) = no characters; empty = automatic. */
  cast: string[];
  /** The drawn lettering of the hero lines and the button, by language then slot (`kicker`, `hero`, `sub`, `cta`); the
   *  lettering op writes it. Every fork keeps it: a slot whose copy changed shows the live text until it is drawn
   *  again (spark-creatives says which slots are stale, see {@link planLandingRun}). */
  lettering: Record<string, Record<string, LandingLettering>>;
  /** The regular page's game tiles (the brand's own games, six to eight). */
  games: LandingGameTile[];
}

/** One read-back of a hero render by the vision check. */
export interface LandingHeroRead {
  url: string;
  /** `passed` | `failed` | `needs_review`. */
  status: string;
  notes?: string;
}

/** Why the generated hero is not `passed`: the first thing the check found on the kept render (`text`, `logo`,
 *  `numbers`), or `unavailable` (the check could not run or its answer was unreadable). */
export type LandingHeroQAReason = 'text' | 'logo' | 'numbers' | 'unavailable';

/** What the vision check made of the generated hero (it must carry no text, logo or numbers): `passed`, `failed`
 *  (still found some after its free redraws) or `needs_review` (it could not read the render). */
export interface LandingHeroQA {
  status: string;
  /** Why it is not passed; absent when passed, and on a hero checked before reasons were recorded (or with a reason
   *  this build does not know), which the composer words generally. */
  reason?: LandingHeroQAReason;
  /** How many renders the hero took (1 + its redraws). */
  attempts: number;
  reads: LandingHeroRead[];
}

/** What a version's generation drew. `errors` names an op that failed (its credits were refunded). */
export interface LandingVersionAssets {
  hero_url?: string;
  hero_video_url?: string;
  /** The generated hero's vision check (absent before a hero is drawn, or for a hero drawn before the check). */
  hero_qa?: LandingHeroQA;
  /** What the scene runs drew into this version, by planned key ("scene:bg_desktop", "scene:character:1"...): a run
   *  draws (and charges) only the keys it does not hold. */
  scene_drawn?: Record<string, string[]>;
  /** Each drawn scene key's checks, a character's `mascot` included. */
  scene_qa?: Record<string, LandingSceneKeyQA>;
  errors?: Record<string, string>;
  /** Any other record the service wrote (its planner may read it back from a fork): kept untouched, so a fork or an
   *  upsert never wipes it. */
  [record: string]: unknown;
}

/** One landing page version (`ad_versions[]`, newest first). A generated version is locked. */
export interface LandingVersion {
  created_at: string;
  locked?: boolean;
  params: LandingParams;
  assets: LandingVersionAssets;
  art_style?: AdVersionArtStyle;
}

/** A version's content without its identity or lock (an upsert's `ad_version`). */
export type LandingVersionEntry = Omit<LandingVersion, 'created_at' | 'locked'>;

export type LandingPublicationState = 'live' | 'unpublished' | 'failed';

/** Where (and whether) the page is published on the brand's landing domain. */
export interface LandingPublication {
  state: LandingPublicationState;
  /** "/welcome-wheel". */
  path: string;
  /** "https://lp.<domain>/welcome-wheel". */
  url: string;
  lp_domain: string;
  /** admin-api's page id (a 64-bit id, kept as text). */
  page_id: string;
  /** The public landing page id, '' when not known yet. */
  lpid: string;
  /** Which version is live. */
  version_at: string;
  published_at: string;
  error?: string;
}

/** A landing page entity as listed. */
export interface LandingPage {
  id: string;
  tenant_id: string;
  name: string;
  media_type: 'landing_page';
  image_url: string;
  status: string;
  archived: boolean;
  job_id?: string;
  created_at?: string;
  ad_params: LandingParams;
  ad_versions: LandingVersion[];
  publication?: LandingPublication;
}

/** Body for creating (no `id`) or updating a landing page (same version rules as a mini-game). */
export interface LandingPageUpsert {
  id?: string;
  name?: string;
  image_url?: string;
  status?: string;
  job_id?: string;
  ad_params?: LandingParams;
  ad_version?: LandingVersionEntry;
  ad_version_at?: string;
}

/* ---------- endpoint payloads ---------- */

/** A landing host of the brand. `primary` is the one page URLs are built with. */
export interface LandingHost {
  lp_domain: string;
  domain: string;
  primary: boolean;
}

/** One live landing page. `html` is its `full_html` block ('' when the page has no such block). */
export interface LivePage {
  path: string;
  name: string;
  lpid: string;
  /** '' when the brand has no landing host yet. */
  url: string;
  title: string;
  favicon: string;
  moid: string;
  html: string;
  block_types: string[];
}

/** `GET /creatives/tenant/{id}/landing-pages/live`. `hosts` empty: the brand has no landing domain yet. */
export interface LivePages {
  hosts: LandingHost[];
  pages: LivePage[];
}

/** `GET /creatives/tenant/{id}/landing-pages/legal`. */
export interface LandingLegalInfo {
  legal: LandingLegal;
  languages: string[];
  default_language: string;
  /** Whether the website's languages could be read (false: `languages` is a guess or empty). An older server that
   *  does not send it reads as true. */
  languages_known: boolean;
}

/** One marketing offer of the brand. `id` is admin-api's 64-bit id as a string (it does not fit a JS number); the
 *  fields the offer list does not carry are null. */
export interface MarketingOffer {
  id: string;
  moid: string;
  name: string;
  product_id: number | null;
  is_active: boolean;
  is_default: boolean;
  start_date: string | null;
  end_date: string | null;
  bonus_preset_id: string | null;
  reward_id: string | null;
}

/** `GET /creatives/tenant/{id}/landing-pages/offers`. `available: false` (with a short `reason`) when the offers could
 *  not be read; the composer then carries on without one. */
export interface MarketingOffers {
  available: boolean;
  reason: string;
  offers: MarketingOffer[];
}

/* ---------- normalisers (pure) ---------- */

type Rec = Record<string, unknown>;

const isRecord = (value: unknown): value is Rec => typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string => (typeof value === 'string' ? value : '');
const num = (value: unknown, fallback = 0): number =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const bool = (value: unknown): boolean => value === true;
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);
const strings = (value: unknown): string[] => list(value).filter((v): v is string => typeof v === 'string');
const nullableStr = (value: unknown): string | null => (typeof value === 'string' ? value : null);
/** The payload, bare or wrapped in `data`. */
const unwrap = (raw: unknown): Rec => {
  const outer = isRecord(raw) ? raw : {};
  return isRecord(outer.data) ? outer.data : outer;
};

const isGame = (value: unknown): value is LandingGame => value === 'wheel' || value === 'pick';

export const normaliseTemplate = (raw: unknown): LandingTemplate => {
  const r = isRecord(raw) ? raw : {};
  const format: LandingTemplateFormat = r.format === 'immersive' ? 'immersive' : 'page';
  return {
    id: str(r.id),
    name: str(r.name),
    description: str(r.description),
    mood: str(r.mood),
    hero_aspect: str(r.hero_aspect),
    max_steps: num(r.max_steps),
    format,
    ...(isGame(r.game) ? { game: r.game } : {}),
    scene_parts: strings(r.scene_parts).filter((k) => k.trim() !== ''),
    // A catalogue that does not say yet: a full-screen game shows a scene, an offer page its hero.
    scene: typeof r.scene === 'boolean' ? r.scene : format === 'immersive',
    hero: typeof r.hero === 'boolean' ? r.hero : format !== 'immersive',
    aliases: strings(r.aliases)
      .map((id) => id.trim())
      .filter(Boolean),
  };
};

/** Whether a template is a full-screen game. Pure. */
export const isImmersive = (tpl: Pick<LandingTemplate, 'format'> | undefined): boolean => tpl?.format === 'immersive';

/** The regular offer page: the default for a new page, and the one template with game tiles, a "why play here" row and
 *  an FAQ. */
export const LANDING_REGULAR_TEMPLATE = 'regular';

/** Whether a template is the regular offer page. Pure. */
export const isRegularTemplate = (tpl: Pick<LandingTemplate, 'id'> | undefined): boolean =>
  tpl?.id === LANDING_REGULAR_TEMPLATE;

/** The template a page's `template_id` names: the template of that id, else the one listing it among its aliases (a
 *  retired id opens its successor); undefined when none does. Pure. */
export const resolveLandingTemplate = <T extends Pick<LandingTemplate, 'id' | 'aliases'>>(
  templates: readonly T[],
  id: string,
): T | undefined => {
  if (!id) return undefined;
  return templates.find((tpl) => tpl.id === id) ?? templates.find((tpl) => tpl.aliases.includes(id));
};

/** The id a page is saved with: a retired id becomes its successor's, any other is kept as it is. Pure. */
export const canonicalTemplateId = (
  templates: readonly Pick<LandingTemplate, 'id' | 'aliases'>[],
  id: string,
): string => resolveLandingTemplate(templates, id)?.id ?? id;

/** The templates in the order they are offered: the full-screen games first, in the catalogue's own order, then the
 *  regular offer page. Pure. */
export const orderedTemplates = <T extends Pick<LandingTemplate, 'id'>>(templates: readonly T[]): T[] => [
  ...templates.filter((tpl) => !isRegularTemplate(tpl)),
  ...templates.filter((tpl) => isRegularTemplate(tpl)),
];

const normaliseLegalText = (raw: unknown): LandingLegalText => {
  const r = isRecord(raw) ? raw : {};
  return {
    licence_html: str(r.licence_html),
    copyright_html: str(r.copyright_html),
    responsible_html: str(r.responsible_html),
  };
};

export const normaliseLegal = (raw: unknown): LandingLegal => {
  const r = isRecord(raw) ? raw : {};
  const source = str(r.source);
  const texts = isRecord(r.texts) ? r.texts : {};
  return {
    source: source === 'site' || source === 'platform' ? source : 'none',
    texts: Object.fromEntries(Object.entries(texts).map(([lang, text]) => [lang, normaliseLegalText(text)])),
    images: list(r.images).map((img) => {
      const i = isRecord(img) ? img : {};
      return { src: str(i.src), alt: str(i.alt), href: str(i.href) };
    }),
  };
};

export const normaliseLivePages = (raw: unknown): LivePages => {
  const d = unwrap(raw);
  return {
    hosts: list(d.hosts).map((h) => {
      const r = isRecord(h) ? h : {};
      return { lp_domain: str(r.lp_domain), domain: str(r.domain), primary: bool(r.primary) };
    }),
    pages: list(d.pages).map((p) => {
      const r = isRecord(p) ? p : {};
      return {
        path: str(r.path),
        name: str(r.name),
        lpid: str(r.lpid),
        url: str(r.url),
        title: str(r.title),
        favicon: str(r.favicon),
        moid: str(r.moid),
        html: str(r.html),
        block_types: strings(r.block_types),
      };
    }),
  };
};

export const normaliseLegalInfo = (raw: unknown): LandingLegalInfo => {
  const d = unwrap(raw);
  const languages = strings(d.languages).filter((l) => l.trim() !== '');
  const defaultLanguage = str(d.default_language) || languages[0] || '';
  return {
    legal: normaliseLegal(d.legal),
    languages,
    default_language: defaultLanguage,
    languages_known: typeof d.languages_known === 'boolean' ? d.languages_known : true,
  };
};

export const normaliseOffers = (raw: unknown): MarketingOffers => {
  const d = unwrap(raw);
  return {
    available: bool(d.available),
    reason: str(d.reason),
    offers: list(d.offers)
      .map((o) => {
        const r = isRecord(o) ? o : {};
        return {
          id: typeof r.id === 'number' ? String(r.id) : str(r.id),
          moid: str(r.moid),
          name: str(r.name),
          product_id: typeof r.product_id === 'number' && Number.isFinite(r.product_id) ? r.product_id : null,
          is_active: bool(r.is_active),
          is_default: bool(r.is_default),
          start_date: nullableStr(r.start_date),
          end_date: nullableStr(r.end_date),
          bonus_preset_id: nullableStr(r.bonus_preset_id),
          reward_id: nullableStr(r.reward_id),
        };
      })
      .filter((o) => o.moid !== ''),
  };
};

const isSubjectType = (value: unknown): value is AdSubjectType =>
  value === 'promotion' || value === 'game' || value === 'gamification';

const normaliseCopy = (raw: unknown): LandingCopy => {
  const r = isRecord(raw) ? raw : {};
  return {
    badge: str(r.badge),
    eyebrow: str(r.eyebrow),
    headline: str(r.headline),
    subline: str(r.subline),
    cta: str(r.cta),
    micro: str(r.micro),
    steps_title: str(r.steps_title),
    steps: strings(r.steps),
    terms_title: str(r.terms_title),
    terms_html: str(r.terms_html),
    key_terms: str(r.key_terms),
    usps: list(r.usps).map((usp) => {
      const u = isRecord(usp) ? usp : {};
      return { title: str(u.title), text: str(u.text) };
    }),
    games_title: str(r.games_title),
    faq_title: str(r.faq_title),
    faq: list(r.faq).map((item) => {
      const f = isRecord(item) ? item : {};
      return { q: str(f.q), a: str(f.a) };
    }),
    closing: str(r.closing),
    hint: str(r.hint),
    disclaimer: str(r.disclaimer),
    prizes: list(r.prizes).map((prize) => {
      const p = isRecord(prize) ? prize : {};
      return { big: str(p.big), small: str(p.small) };
    }),
    decoys: strings(r.decoys),
    modal_title: str(r.modal_title),
    modal_text: str(r.modal_text),
    hub: str(r.hub),
  };
};

/** The page's faces ('' each when the wire carries none: the service resolves them). */
export const normaliseTypography = (raw: unknown): LandingTypography => {
  const r = isRecord(raw) ? raw : {};
  return { display: str(r.display).trim(), body: str(r.body).trim() };
};

const normaliseFontSrc = (raw: unknown): LandingFontSrc | null => {
  const r = isRecord(raw) ? raw : {};
  const url = str(r.url);
  if (!url) return null;
  return {
    url,
    ...(str(r.weight) ? { weight: str(r.weight) } : {}),
    ...(str(r.style) ? { style: str(r.style) } : {}),
    ...(str(r.format) ? { format: str(r.format) } : {}),
  };
};

const normaliseFontFace = (raw: unknown): LandingFontFace | null => {
  const r = isRecord(raw) ? raw : {};
  const family = str(r.family).trim();
  if (!family) return null;
  const faces = list(r.faces)
    .map(normaliseFontSrc)
    .filter((f): f is LandingFontSrc => f !== null);
  return { family, ...(str(r.href) ? { href: str(r.href) } : {}), ...(faces.length > 0 ? { faces } : {}) };
};

/** The brand as the renderer sees it (bare or wrapped in `data`); every missing field its empty value. */
export const normaliseBrand = (raw: unknown): LandingBrand => {
  const r = unwrap(raw);
  return {
    name: str(r.name),
    domain: str(r.domain),
    logo_url: str(r.logo_url),
    favicon_url: str(r.favicon_url),
    button_fill: str(r.button_fill),
    button_text: str(r.button_text),
    button_radius_px: num(r.button_radius_px, -1),
    button_pill: bool(r.button_pill),
    primary: str(r.primary),
    secondary: str(r.secondary),
    background: str(r.background),
    text: str(r.text),
    font_heading: str(r.font_heading).trim(),
    font_body: str(r.font_body).trim(),
    fonts: list(r.fonts)
      .map(normaliseFontFace)
      .filter((f): f is LandingFontFace => f !== null),
    display_suggestion: str(r.display_suggestion).trim(),
  };
};

/** How many characters a scene shows (left and right of the game). */
export const LANDING_CAST_MAX = 2;
/** The cast that shows no characters. */
export const LANDING_CAST_NONE = 'none';

/** A cast from the wire: mascot ids, each once, at most {@link LANDING_CAST_MAX}; `['none']` when it says none; [] for
 *  automatic. Pure. */
export const normaliseCast = (raw: unknown): string[] => {
  const ids = Array.from(new Set(strings(raw).map((id) => id.trim()))).filter(Boolean);
  if (ids.includes(LANDING_CAST_NONE)) return [LANDING_CAST_NONE];
  return ids.slice(0, LANDING_CAST_MAX);
};

/** Whether a cast shows no characters. Pure. */
export const isCastNone = (cast: readonly string[]): boolean => cast.length === 1 && cast[0] === LANDING_CAST_NONE;

/** Whether two casts are the same pick (same ids, same order). Pure. */
export const castEqual = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/** The document a preview renders: as edited, except that a cast of no characters shows none at once (the scene's
 *  drawn characters stay stored on the version). Pure. */
export const landingPreviewParams = (params: LandingParams): LandingParams =>
  isCastNone(params.cast) && params.scene.characters.length > 0
    ? { ...params, scene: { ...params.scene, characters: [] } }
    : params;

/** A blank scene (nothing drawn yet). Pure. */
export const emptyScene = (): LandingScene => ({
  background_desktop_url: '',
  background_mobile_url: '',
  characters: [],
  symbols: [],
  parts: {},
});

const isPartMeta = (value: unknown): value is LandingPartMeta =>
  isRecord(value) && (value.bulbs === undefined || Array.isArray(value.bulbs));

const isSceneKeyQA = (value: unknown): value is LandingSceneKeyQA =>
  isRecord(value) && (value.mascot === undefined || typeof value.mascot === 'string');

export const normaliseScene = (raw: unknown): LandingScene => {
  const r = isRecord(raw) ? raw : {};
  const parts = isRecord(r.parts) ? r.parts : {};
  const partMeta = isRecord(r.part_meta)
    ? Object.fromEntries(Object.entries(r.part_meta).filter((e): e is [string, LandingPartMeta] => isPartMeta(e[1])))
    : {};
  return {
    background_desktop_url: str(r.background_desktop_url),
    background_mobile_url: str(r.background_mobile_url),
    characters: strings(r.characters).filter(Boolean),
    symbols: strings(r.symbols).filter(Boolean),
    parts: Object.fromEntries(
      Object.entries(parts).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== ''),
    ),
    ...(Object.keys(partMeta).length > 0 ? { part_meta: partMeta } : {}),
  };
};

/** A `{key: url}` map with only its string values; undefined when none is left. */
const urlMap = (raw: unknown): Record<string, string> | undefined => {
  if (!isRecord(raw)) return undefined;
  const entries = Object.entries(raw).filter((e): e is [string, string] => typeof e[1] === 'string' && e[1] !== '');
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

/** A lettering's core box: four numbers (0 each where the wire says none). Pure. */
const normaliseCore = (raw: unknown): [number, number, number, number] => {
  const v = list(raw);
  return [num(v[0]), num(v[1]), num(v[2]), num(v[3])];
};

/** The drawn lettering by language then slot, as the service wrote it; a slot with no image is dropped. Pure. */
export const normaliseLettering = (raw: unknown): Record<string, Record<string, LandingLettering>> => {
  if (!isRecord(raw)) return {};
  const out: Record<string, Record<string, LandingLettering>> = {};
  for (const [lang, slots] of Object.entries(raw)) {
    if (!isRecord(slots)) continue;
    const kept: Record<string, LandingLettering> = {};
    for (const [slot, value] of Object.entries(slots)) {
      if (!isRecord(value) || !str(value.url)) continue;
      kept[slot] = {
        url: str(value.url),
        text: str(value.text),
        core: normaliseCore(value.core),
        w: num(value.w),
        h: num(value.h),
      };
    }
    if (Object.keys(kept).length > 0) out[lang] = kept;
  }
  return out;
};

/** The regular page's game tiles; a tile with no id is dropped. Pure. */
export const normaliseGameTiles = (raw: unknown): LandingGameTile[] =>
  list(raw)
    .map((game) => {
      const g = isRecord(game) ? game : {};
      return {
        id: typeof g.id === 'number' ? String(g.id) : str(g.id),
        name: str(g.name),
        image_url: str(g.image_url),
      };
    })
    .filter((g) => g.id !== '');

/** What a page features, as the wire holds it: a known type with its items (each with an id, at most one promotion and
 *  {@link LANDING_FEATURED_GAMES_MAX} games); undefined when it names no type or no item (the welcome offer leads).
 *  Pure. */
export const normaliseFeatured = (raw: unknown): LandingFeatured | undefined => {
  if (!isRecord(raw)) return undefined;
  const type: LandingFeaturedType | undefined =
    raw.type === 'promotion' ? 'promotion' : raw.type === 'game' ? 'game' : undefined;
  if (!type) return undefined;
  const seen = new Set<string>();
  const items = list(raw.items)
    .map(normaliseAdSubject)
    .filter((item) => {
      if (!item.id || seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    })
    .slice(0, type === 'promotion' ? 1 : LANDING_FEATURED_GAMES_MAX);
  return items.length > 0 ? { type, items } : undefined;
};

/** Full landing params from a wire value (a blank draft's `{}` included), so every consumer sees the whole shape. */
export const normaliseLandingParams = (raw: unknown): LandingParams => {
  const r = isRecord(raw) ? raw : {};
  const copy = isRecord(r.copy) ? r.copy : {};
  const offer = isRecord(r.offer) ? r.offer : {};
  const hero = isRecord(r.hero) ? r.hero : {};
  const languages = strings(r.languages).filter((l) => l.trim() !== '');
  const featured = normaliseFeatured(r.featured);
  return {
    template_id: str(r.template_id),
    languages,
    default_language: str(r.default_language) || languages[0] || '',
    opening_language: str(r.opening_language),
    copy: Object.fromEntries(Object.entries(copy).map(([lang, c]) => [lang, normaliseCopy(c)])),
    offer: { moid: str(offer.moid), name: str(offer.name) },
    cta_url: str(r.cta_url),
    hero: { image_url: str(hero.image_url), video_url: str(hero.video_url), poster_url: str(hero.poster_url) },
    payment_icons: strings(r.payment_icons),
    title: str(r.title),
    scene: normaliseScene(r.scene),
    subject_type: isSubjectType(r.subject_type) ? r.subject_type : 'promotion',
    subject: normaliseAdSubject(r.subject),
    ...(featured ? { featured } : {}),
    art_direction: str(r.art_direction),
    typography: normaliseTypography(r.typography),
    cast: normaliseCast(r.cast),
    lettering: normaliseLettering(r.lettering),
    games: normaliseGameTiles(r.games),
  };
};

const HERO_QA_REASONS: readonly string[] = ['text', 'logo', 'numbers', 'unavailable'];

const isHeroQAReason = (value: unknown): value is LandingHeroQAReason =>
  typeof value === 'string' && HERO_QA_REASONS.includes(value);

/** A hero's vision check; undefined when there is none (no status). */
const normaliseHeroQA = (raw: unknown): LandingHeroQA | undefined => {
  if (!isRecord(raw) || !str(raw.status)) return undefined;
  return {
    status: str(raw.status),
    ...(isHeroQAReason(raw.reason) ? { reason: raw.reason } : {}),
    attempts: num(raw.attempts),
    reads: list(raw.reads).map((read) => {
      const r = isRecord(read) ? read : {};
      return { url: str(r.url), status: str(r.status), ...(str(r.notes) ? { notes: str(r.notes) } : {}) };
    }),
  };
};

/** The asset records this build reads (normalised); every other key is passed through as the service wrote it. */
const KNOWN_ASSET_KEYS: readonly string[] = [
  'hero_url',
  'hero_video_url',
  'hero_qa',
  'scene_drawn',
  'scene_qa',
  'errors',
];

const normaliseVersionAssets = (raw: unknown): LandingVersionAssets => {
  const r = isRecord(raw) ? raw : {};
  const other = Object.fromEntries(Object.entries(r).filter(([key]) => !KNOWN_ASSET_KEYS.includes(key)));
  const errors = urlMap(r.errors);
  const heroQA = normaliseHeroQA(r.hero_qa);
  // The scene's records are kept as the service wrote them (its planner reads them back from a fork).
  const sceneDrawn = isRecord(r.scene_drawn)
    ? Object.fromEntries(Object.entries(r.scene_drawn).map(([key, urls]): [string, string[]] => [key, strings(urls)]))
    : undefined;
  const sceneQA = isRecord(r.scene_qa)
    ? Object.fromEntries(Object.entries(r.scene_qa).filter((e): e is [string, LandingSceneKeyQA] => isSceneKeyQA(e[1])))
    : undefined;
  return {
    ...other,
    ...(str(r.hero_url) ? { hero_url: str(r.hero_url) } : {}),
    ...(str(r.hero_video_url) ? { hero_video_url: str(r.hero_video_url) } : {}),
    ...(heroQA ? { hero_qa: heroQA } : {}),
    ...(sceneDrawn ? { scene_drawn: sceneDrawn } : {}),
    ...(sceneQA ? { scene_qa: sceneQA } : {}),
    ...(errors ? { errors } : {}),
  };
};

const normaliseLandingVersion = (raw: unknown): LandingVersion | null => {
  if (!isRecord(raw) || typeof raw.created_at !== 'string') return null;
  const artStyle = normaliseVersionArtStyle(raw.art_style);
  return {
    created_at: raw.created_at,
    locked: raw.locked === true,
    params: normaliseLandingParams(raw.params),
    assets: normaliseVersionAssets(raw.assets),
    ...(artStyle ? { art_style: artStyle } : {}),
  };
};

/** A 64-bit id as text (a number that came through JSON is turned back into its digits). */
const idText = (value: unknown): string => (typeof value === 'number' ? String(value) : str(value));

export const normalisePublication = (raw: unknown): LandingPublication | undefined => {
  if (!isRecord(raw)) return undefined;
  const state = str(raw.state);
  if (state !== 'live' && state !== 'unpublished' && state !== 'failed') return undefined;
  return {
    state,
    path: str(raw.path),
    url: str(raw.url),
    lp_domain: str(raw.lp_domain),
    page_id: idText(raw.page_id),
    lpid: str(raw.lpid),
    version_at: str(raw.version_at),
    published_at: str(raw.published_at),
    ...(str(raw.error) ? { error: str(raw.error) } : {}),
  };
};

/** A landing page row with its params, versions and publication normalised. */
export const normaliseLandingPage = (raw: unknown): LandingPage => {
  const r = isRecord(raw) ? raw : {};
  const publication = normalisePublication(r.publication);
  return {
    id: str(r.id),
    tenant_id: idText(r.tenant_id),
    name: str(r.name),
    media_type: 'landing_page',
    image_url: str(r.image_url),
    status: str(r.status) || 'draft',
    archived: r.archived === true || r.status === 'archived',
    ...(str(r.job_id) ? { job_id: str(r.job_id) } : {}),
    ...(str(r.created_at) ? { created_at: str(r.created_at) } : {}),
    ad_params: normaliseLandingParams(r.ad_params),
    ad_versions: list(r.ad_versions)
      .map(normaliseLandingVersion)
      .filter((v): v is LandingVersion => v !== null),
    ...(publication ? { publication } : {}),
  };
};

/* ---------- requests (never throw unless named so) ---------- */

const tenantPath = (tenantId: number, rest: string): string =>
  `/creatives/tenant/${encodeURIComponent(String(tenantId))}/landing-pages/${rest}`;

/** Map a successful answer through a normaliser, passing a refusal through unchanged. */
const mapOk = <T>(res: AdApiResult<Rec>, normalise: (raw: unknown) => T): AdApiResult<T> =>
  res.ok ? { ok: true, data: normalise(res.data) } : res;

/** The brand's live landing pages and its landing hosts. `includeHtml: false` asks for the pages without their HTML
 *  (each `html` then reads ''), for a caller that only needs their names and addresses. */
export const fetchLivePages = async (
  tenantId: number,
  signal?: AbortSignal,
  opts?: { includeHtml?: boolean },
): Promise<AdApiResult<LivePages>> =>
  mapOk(
    await adApiRequest<Rec>(
      'get',
      tenantPath(tenantId, opts?.includeHtml === false ? 'live?include_html=false' : 'live'),
      undefined,
      signal,
    ),
    normaliseLivePages,
  );

/** The live pages without their HTML, throwing when they cannot be read (for the Marketing hub card, which only
 *  counts them, and then says nothing rather than "none yet"). */
export const readLivePages = async (tenantId: number, signal?: AbortSignal): Promise<LivePages> => {
  const res = await fetchLivePages(tenantId, signal, { includeHtml: false });
  if (!res.ok) throw new Error(res.error || `landing pages unavailable (${res.status})`);
  return res.data;
};

/** The website's footer legal text and the brand's languages. */
export const fetchLandingLegal = async (
  tenantId: number,
  signal?: AbortSignal,
): Promise<AdApiResult<LandingLegalInfo>> =>
  mapOk(await adApiRequest<Rec>('get', tenantPath(tenantId, 'legal'), undefined, signal), normaliseLegalInfo);

/** The brand's marketing offers (answered with `available: false` rather than an error when they cannot be read). */
export const fetchMarketingOffers = async (
  tenantId: number,
  signal?: AbortSignal,
): Promise<AdApiResult<MarketingOffers>> =>
  mapOk(await adApiRequest<Rec>('get', tenantPath(tenantId, 'offers'), undefined, signal), normaliseOffers);

/** The brand as the renderer sees it: its colours, button and fonts, the website's own faces and the display face
 *  matched to it (`fonts` / `display_suggestion` read empty while the service does not send them). Free. */
export const fetchLandingBrand = async (tenantId: number, signal?: AbortSignal): Promise<AdApiResult<LandingBrand>> =>
  mapOk(await adApiRequest<Rec>('get', tenantPath(tenantId, 'brand'), undefined, signal), normaliseBrand);

// The template catalogue is static per deploy: read once per page load and shared. A failed or empty read is dropped
// so the next caller retries.
let templatesRequest: Promise<LandingTemplate[] | null> | null = null;

/** The premium template catalogue, in display order; null when it could not be read. Never throws. */
export const fetchLandingTemplates = (): Promise<LandingTemplate[] | null> => {
  if (templatesRequest) return templatesRequest;
  const request = adApiRequest<Rec>('get', '/creatives/landing-pages/templates').then((res) => {
    if (!res.ok) {
      templatesRequest = null;
      return null;
    }
    const templates = list(unwrap(res.data).templates)
      .map(normaliseTemplate)
      .filter((tpl) => tpl.id !== '');
    // An empty catalogue is not kept: the next open asks again (templates may be deployed meanwhile).
    if (templates.length === 0) templatesRequest = null;
    return templates;
  });
  templatesRequest = request;
  return request;
};

/** The page rendered as a complete HTML document (brand and legal resolved server-side; a game is playable in it).
 *  `assets` are a version's drawn art (its hero stands in for an empty hero). Free. A 400 means the template is
 *  unknown. */
export const previewLandingPage = async (
  tenantId: number,
  params: LandingParams,
  signal?: AbortSignal,
  assets?: LandingVersionAssets,
): Promise<AdApiResult<{ html: string }>> =>
  mapOk(
    await adApiRequest<Rec>('post', tenantPath(tenantId, 'preview'), { params, ...(assets ? { assets } : {}) }, signal),
    (raw) => ({ html: str(unwrap(raw).html) }),
  );

/** The Studio colours a template card's sample preview is drawn in (#rrggbb each), in place of the brand's palette. */
export interface LandingStudioColours {
  primary: string;
  accent: string;
  background: string;
  text: string;
  button_fill: string;
  button_text: string;
}

/** The Studio's resolved colours ({@link StudioColours}) as a sample preview's `studio_colours`. Pure. */
export const landingStudioColours = (c: StudioColours): LandingStudioColours => ({
  primary: c.primary,
  accent: c.accent,
  background: c.bg,
  text: c.text,
  button_fill: c.ctaFill,
  button_text: c.ctaText,
});

/** A template card's document while spark-creatives cannot draw a sample: the page on that template (a game's own copy
 *  seeded) with none of its drawn art (no scene, no lettering, no hero), so the template's built-in drawings show.
 *  Pure. */
export const landingSampleParams = (
  params: LandingParams,
  template: Pick<LandingTemplate, 'id' | 'format' | 'game'>,
): LandingParams => {
  const base: LandingParams = {
    ...params,
    template_id: template.id,
    scene: emptyScene(),
    lettering: {},
    hero: { image_url: '', video_url: '', poster_url: '' },
    cast: [],
  };
  return isImmersive(template) ? withImmersiveCopy(withoutGameLines(base), template.game) : base;
};

/** A template card's preview (`sample: true`): spark-creatives renders the template with sample copy in the brand's
 *  default language and a neutral welcome offer, in the Studio's colours (`colours`; the brand's palette when null),
 *  with the brand's own logo and name and none of its scene art. A service that cannot draw a sample yet (any refusal)
 *  is asked for the page's own preview instead (`params`, see {@link landingSampleParams}). Free. */
export const previewLandingSample = async (
  tenantId: number,
  params: LandingParams,
  colours: LandingStudioColours | null,
  signal?: AbortSignal,
): Promise<AdApiResult<{ html: string }>> => {
  const sample = mapOk(
    await adApiRequest<Rec>(
      'post',
      tenantPath(tenantId, 'preview'),
      { params, sample: true, ...(colours ? { studio_colours: colours } : {}) },
      signal,
    ),
    (raw) => ({ html: str(unwrap(raw).html) }),
  );
  if (sample.ok ? sample.data.html !== '' : sample.aborted) return sample;
  return previewLandingPage(tenantId, params, signal);
};

/** One key a Generate would draw and charge ("copy", "scene:bg_desktop", "lettering:hero:es"...). */
export interface LandingPlanKey {
  key: string;
  op: string;
  credits: number;
}

/** What a Generate of the document would run, as spark-creatives plans it (the same planner the Generate runs). */
export interface LandingRunPlan {
  keys: LandingPlanKey[];
  total_credits: number;
  /** Per op: how many keys it draws and what they cost. */
  ops: Record<string, { keys: number; credits: number }>;
  /** Some lettering is missing or no longer reads the copy (the page shows the live text there until it is drawn). */
  lettering_stale: boolean;
}

/** The document a plan is asked for: the composer's current (possibly unsaved) state, like a preview. */
export interface LandingPlanRequest {
  landing_page_id: string;
  ops: LandingOp[];
  params: LandingParams;
  assets: LandingVersionAssets;
  /** The art style the run draws in (the Generate's `style_id`); absent: the brand's active one. */
  style_id?: string;
  /** The lead character (the Generate's `mascot_id`: 'none' or a mascot id); absent: automatic. */
  mascot_id?: string;
}

/** A plan answer (bare or wrapped in `data`); the total is the keys' sum when the answer does not carry one. Pure. */
export const normaliseRunPlan = (raw: unknown): LandingRunPlan => {
  const d = unwrap(raw);
  const keys = list(d.keys)
    .map((k) => {
      const r = isRecord(k) ? k : {};
      return { key: str(r.key), op: str(r.op), credits: num(r.credits) };
    })
    .filter((k) => k.key !== '');
  const ops = isRecord(d.ops)
    ? Object.fromEntries(
        Object.entries(d.ops).map(([op, v]): [string, { keys: number; credits: number }] => {
          const r = isRecord(v) ? v : {};
          return [op, { keys: num(r.keys), credits: num(r.credits) }];
        }),
      )
    : {};
  return {
    keys,
    total_credits: num(
      d.total_credits,
      keys.reduce((sum, k) => sum + k.credits, 0),
    ),
    ops,
    lettering_stale: bool(d.lettering_stale),
  };
};

/** What a Generate of the document would draw and cost (`POST .../landing-pages/plan`): spark-creatives runs the
 *  Generate's own planner on it, so the cost and the lettering's staleness are the service's, never re-derived here.
 *  Free. */
export const planLandingRun = async (
  tenantId: number,
  body: LandingPlanRequest,
  signal?: AbortSignal,
): Promise<AdApiResult<LandingRunPlan>> =>
  mapOk(await adApiRequest<Rec>('post', tenantPath(tenantId, 'plan'), body, signal), normaliseRunPlan);

const entityRoot = (tenantId: number): string =>
  `/creatives/tenant/${encodeURIComponent(String(tenantId))}/landing-pages`;
const entityPath = (tenantId: number, id: string): string => `${entityRoot(tenantId)}/${encodeURIComponent(id)}`;

/** The rows of a list answer: `{ landing_pages: [] }`, `{ data: [] }` or `{ data: { landing_pages: [] } }`. */
const listRows = (raw: unknown): unknown[] => {
  const outer = isRecord(raw) ? raw : {};
  if (Array.isArray(outer.landing_pages)) return outer.landing_pages;
  if (Array.isArray(outer.data)) return outer.data;
  return list(unwrap(raw).landing_pages);
};

/** The brand's landing pages (archived ones flagged), newest first. */
export const fetchLandingPages = async (tenantId: number, signal?: AbortSignal): Promise<AdApiResult<LandingPage[]>> =>
  mapOk(await adApiRequest<Rec>('get', entityRoot(tenantId), undefined, signal), (raw) =>
    listRows(raw)
      .map(normaliseLandingPage)
      .filter((p) => p.id !== ''),
  );

/** The brand's landing pages, THROWING when they cannot be read (so a library keeps its last list on screen). */
export const listLandingPages = async (tenantId: number, signal?: AbortSignal): Promise<LandingPage[]> => {
  const res = await fetchLandingPages(tenantId, signal);
  if (!res.ok) throw new Error(res.error || `landing pages unavailable (${res.status})`);
  return res.data;
};

/** One landing page by id. */
export const fetchLandingPage = async (
  tenantId: number,
  id: string,
  signal?: AbortSignal,
): Promise<AdApiResult<LandingPage>> =>
  mapOk(await adApiRequest<Rec>('get', entityPath(tenantId, id), undefined, signal), (raw) =>
    normaliseLandingPage(unwrap(raw)),
  );

/** Create (no id) or update a landing page. Answers its id. */
export const upsertLandingPage = async (
  tenantId: number,
  body: LandingPageUpsert,
  signal?: AbortSignal,
): Promise<AdApiResult<string>> => {
  const res = await adApiRequest<Rec>('post', entityRoot(tenantId), body, signal);
  if (!res.ok) return res;
  const id = idText(res.data.id) || idText(unwrap(res.data).id) || body.id || '';
  return id ? { ok: true, data: id } : { ok: false, status: 0, error: 'the landing page answer carried no id' };
};

/** The refusal a publish answers when another live page of the brand uses the path (409, code `path_taken`; the
 *  route's only conflict). */
export const PATH_TAKEN = 'path_taken';

/** Publish a version of the page at `path` on the brand's landing domain (no `version_at`: the newest version;
 *  republishing updates the same page). A refusal with `error` {@link PATH_TAKEN}: another live page of the brand uses
 *  the path; 400 a bad path, 404 an unknown version. A landing-app failure after the checks answers 200 with
 *  `publication.state: 'failed'` and its `error` (a retry is a publish again). */
export const publishLandingPage = async (
  tenantId: number,
  id: string,
  body: { path: string; version_at?: string },
  signal?: AbortSignal,
): Promise<AdApiResult<LandingPage>> => {
  const res = await adApiRequest<Rec>('post', `${entityPath(tenantId, id)}/publish`, body, signal);
  if (!res.ok) return res.status === 409 ? { ...res, error: PATH_TAKEN } : res;
  return { ok: true, data: normaliseLandingPage(unwrap(res.data)) };
};

/** Take the page off the landing domain: the landing app's page is DELETED (an inactive one would still be served), so
 *  its address sends visitors to the casino until a republish, which creates a new page (a new public id) at the same
 *  address. Path and address are kept. Can take several seconds. */
export const unpublishLandingPage = async (
  tenantId: number,
  id: string,
  signal?: AbortSignal,
): Promise<AdApiResult<LandingPage>> =>
  mapOk(await adApiRequest<Rec>('post', `${entityPath(tenantId, id)}/unpublish`, {}, signal), (raw) =>
    normaliseLandingPage(unwrap(raw)),
  );

/* ---------- pure helpers ---------- */

/** The copy a new page starts with in English: the document seed (Lumio rewrites it), worded so the first preview
 *  already reads like a finished page. Every other language starts from its own translation of it
 *  ({@link landingSeedCopy}). */
export const DEFAULT_LANDING_COPY: LandingCopy = {
  badge: 'Welcome bonus',
  eyebrow: 'Up to',
  headline: '100% bonus',
  subline: '+ 100 free spins',
  cta: 'Claim your bonus',
  micro: '18+ · T&Cs apply',
  steps_title: 'How it works',
  steps: ['Create your account in under a minute', 'Make your first deposit', 'Your bonus lands in your account'],
  terms_title: 'Terms and conditions',
  // No terms of our own: spark-creatives shows the welcome promotion's own terms from the website at every preview and
  // publish (a seeded paragraph here was once taken for the owner's terms and published).
  terms_html: '',
  key_terms: '18+ · New players only · T&Cs apply',
  usps: [
    { title: 'Quick sign-up', text: 'Join in a few steps' },
    { title: 'Local payments', text: 'Pay with the methods you already use' },
    { title: 'Friendly support', text: 'Help when you need it' },
    { title: 'Play anywhere', text: 'On your phone or your computer' },
  ],
  games_title: 'Popular games',
  faq_title: 'Questions',
  faq: [
    {
      q: 'How do I claim the bonus?',
      a: 'Create your account, make your first deposit and the bonus lands in your account.',
    },
    { q: 'Are there wagering requirements?', a: 'The bonus terms on our website say how the bonus works.' },
    { q: 'Which payment methods can I use?', a: 'Every payment method shown on our website.' },
    { q: 'Who can claim this offer?', a: 'New players aged 18 or over.' },
  ],
  closing: 'Your bonus is waiting',
  hint: '',
  disclaimer: '',
  prizes: [],
  decoys: [],
  modal_title: '',
  modal_text: '',
  hub: '',
};

/** A copy of a page copy (its lists included), so an edit to one language never reaches another. Pure. */
export const cloneCopy = (copy: LandingCopy): LandingCopy => ({
  ...copy,
  steps: [...copy.steps],
  usps: copy.usps.map((u) => ({ ...u })),
  faq: copy.faq.map((f) => ({ ...f })),
  prizes: copy.prizes.map((p) => ({ ...p })),
  decoys: [...copy.decoys],
});

/** The copy a page language starts with (a new page's, or a language added to it): the seed of its base language
 *  ({@link adLanguageBase}: `mx` and `cl` Spanish, `br` Portuguese, `cfr` French, `za` Afrikaans, an unknown id
 *  English), so no language ever starts with another language's text. English is {@link DEFAULT_LANDING_COPY}, the
 *  others landing-seed-copy.ts. A fresh copy. Pure. */
export const landingSeedCopy = (language: string): LandingCopy => {
  const base = adLanguageBase(language);
  return cloneCopy(base === 'en' ? DEFAULT_LANDING_COPY : LANDING_SEED_COPY[base]);
};

/* ---------- immersive (full-screen game) copy seeds (pure) ---------- */

/** How many prizes an immersive reveal plays (one per play). */
export const LANDING_PRIZES_MIN = 1;
export const LANDING_PRIZES_MAX = 3;

/** An immersive page's seed words in one language: the call to play per game (and for any game), the small print, the
 *  win modal, and the words of the reveal (free spins, spins not said to be free, the bonus) and of its cashback decoy.
 *  AD CONTENT in the page's language, never Studio UI copy (no i18n). */
export interface LandingImmersiveSeedText {
  hints: Record<LandingGame, string>;
  hint_any: string;
  disclaimer: string;
  modal_title: string;
  modal_text: string;
  free_spins: string;
  spins: string;
  bonus: string;
  cashback: string;
}

/** The English immersive seed (the document seed, rewritten by Lumio); the other languages are in
 *  landing-seed-copy.ts. */
const LANDING_IMMERSIVE_SEED_EN: LandingImmersiveSeedText = {
  hints: { wheel: 'Spin the wheel and claim your bonus!', pick: 'Pick a prize and claim your bonus!' },
  hint_any: 'Play and claim your bonus!',
  disclaimer: '18+ new players only. T&Cs apply. The game is illustrative.',
  modal_title: 'Congratulations!',
  modal_text: 'Your welcome bonus is waiting',
  free_spins: 'free spins',
  spins: 'spins',
  bonus: 'welcome bonus',
  cashback: '20% cashback',
};

/** The immersive seed words of a page language (its base language's, as {@link landingSeedCopy}). Pure. */
const immersiveSeedText = (language: string): LandingImmersiveSeedText => {
  const base = adLanguageBase(language);
  return base === 'en' ? LANDING_IMMERSIVE_SEED_EN : LANDING_IMMERSIVE_SEEDS[base];
};

/** Every language's immersive seed words (a seeded call to play is recognised in any of them). */
const IMMERSIVE_SEEDS_ALL: readonly LandingImmersiveSeedText[] = [
  LANDING_IMMERSIVE_SEED_EN,
  ...Object.values(LANDING_IMMERSIVE_SEEDS),
];

/** The reveal a new immersive page starts with when nothing names the offer's figures, in a language's words. Pure. */
const defaultReveal = (seed: LandingImmersiveSeedText): LandingPrize[] => [
  { big: '300', small: seed.free_spins },
  { big: '$30,000', small: seed.bonus },
];

/** The reveal a new immersive page starts with when nothing names the offer's figures (English). */
export const DEFAULT_LANDING_PRIZES: readonly LandingPrize[] = defaultReveal(LANDING_IMMERSIVE_SEED_EN);

/** A figure followed by its spins, in the languages a page is seeded in ("220 free spins", "100 giros gratis", "50
 *  rodadas", "100 tours gratuits", "100 gratis draaie", "100 rotiri gratuite", "100 ingyenes pörgetés"). */
const SPINS_RE =
  /(\d[\d.,]*)\s*(free\s*spins|giros(?:\s+gr[aá]tis)?|rodadas(?:\s+gr[aá]tis)?|tiradas(?:\s+gratis)?|tours(?:\s+gratuits)?|gratis\s+draaie|draaie|rotiri(?:\s+gratuite|\s+gratis)?|(?:ingyen(?:es)?\s+)?pörgetés|spins)/i;
/** The words that say the spins are free, in any of those languages. */
const FREE_SPINS_RE = /free|gr[aá]tis|gratuit|ingyen/i;
const CURRENCY_CODES = 'MXN|BRL|USD|EUR|ARS|CLP|COP|PEN|ZAR|GBP|CAD';
const CASH_RE = new RegExp(
  `(?:R\\$|MX\\$|US\\$|S\\/\\.?|[$€£])\\s?\\d[\\d.,]*(?:\\s?(?:${CURRENCY_CODES})\\b)?|\\d[\\d.,]*\\s?(?:[€£]|(?:${CURRENCY_CODES})\\b)`,
  'i',
);
const PERCENT_RE = /\d+\s?%/;

/** The reveal named by an offer's texts ("+ 220 free spins", "100% up to R$6000"): the spins first, then the cash
 *  (else the percentage) as the final, headline prize, each worded in `language` (a page language id; '' English:
 *  "free spins" only when the text says they are free, else "spins"; "welcome bonus"); empty when no text names a
 *  figure. Pure. */
export const prizesFromTexts = (texts: readonly string[], language = ''): LandingPrize[] => {
  const words = immersiveSeedText(language);
  const all = texts.filter(Boolean);
  let spins: LandingPrize | null = null;
  let cash: LandingPrize | null = null;
  let percent: LandingPrize | null = null;
  for (const text of all) {
    const s: RegExpExecArray | null = spins ? null : SPINS_RE.exec(text);
    if (s) spins = { big: s[1], small: FREE_SPINS_RE.test(s[2]) ? words.free_spins : words.spins };
    const c: RegExpExecArray | null = cash ? null : CASH_RE.exec(text);
    if (c) cash = { big: c[0].trim(), small: words.bonus };
    const pc: RegExpExecArray | null = percent ? null : PERCENT_RE.exec(text);
    if (pc) percent = { big: pc[0].replace(/\s+/g, ''), small: words.bonus };
  }
  const final = cash ?? percent;
  return [spins, final].filter((p): p is LandingPrize => p !== null);
};

/** The currency mark a cash prize is written with ("R$", "$", "€"), '$' when none says. Pure. */
const currencyMark = (prizes: readonly LandingPrize[]): string => {
  for (const p of prizes) {
    const m = /^(R\$|MX\$|US\$|S\/\.?|[$€£])/.exec(p.big.trim());
    if (m) return m[1];
  }
  return '$';
};

/** Whether a hint is one of the seeds, in any language (the owner has not written their own). */
const isSeedHint = (hint: string): boolean =>
  IMMERSIVE_SEEDS_ALL.some((seed) => hint === seed.hint_any || Object.values(seed.hints).includes(hint));

/** An immersive page's copy with every EMPTY immersive field seeded (a written one is kept; a seeded call to play
 *  follows the game): the prizes from the promotion's texts, else the copy's own offer, else the default reveal;
 *  decoys in the prizes' currency; the call to play, the small print, the win modal and the reveal's words in the
 *  copy's own `language` (a page language id, its base language's seed: {@link landingSeedCopy}; '' English), the
 *  document seed Lumio rewrites. Pure. */
export const withImmersiveSeeds = (
  copy: LandingCopy,
  game: LandingGame | undefined,
  subjectTexts: readonly string[] = [],
  language = '',
): LandingCopy => {
  const seed = immersiveSeedText(language);
  const fromSubject = prizesFromTexts(subjectTexts, language);
  const fromCopy = prizesFromTexts([copy.subline, copy.headline], language);
  const prizes = copy.prizes.some((p) => p.big.trim() || p.small.trim())
    ? copy.prizes
    : fromSubject.length > 0
      ? fromSubject
      : fromCopy.length > 0
        ? fromCopy
        : defaultReveal(seed);
  const cur = currencyMark(prizes);
  const hint = game ? seed.hints[game] : seed.hint_any;
  return {
    ...copy,
    prizes: prizes.map((p) => ({ ...p })),
    decoys:
      copy.decoys.length > 0
        ? [...copy.decoys]
        : [`${cur}250`, `${cur}500`, `10 ${seed.free_spins}`, `25 ${seed.free_spins}`, seed.cashback],
    hint: !copy.hint.trim() || isSeedHint(copy.hint) ? hint : copy.hint,
    disclaimer: copy.disclaimer.trim() ? copy.disclaimer : seed.disclaimer,
    modal_title: copy.modal_title.trim() ? copy.modal_title : seed.modal_title,
    modal_text: copy.modal_text.trim() ? copy.modal_text : seed.modal_text,
  };
};

/** The promotion's texts an immersive page's reveal is read from (its headline and subtitle). Pure. */
const subjectOfferTexts = (params: Pick<LandingParams, 'subject'>): string[] => [
  params.subject.headline ?? '',
  params.subject.subtitle ?? '',
];

/** The params with the copy lines that name a game (call to play, small print, win modal) cleared in every language,
 *  for a switch to another game: its own words are then seeded or rendered. Prizes and decoys are kept. Pure. */
export const withoutGameLines = (params: LandingParams): LandingParams => ({
  ...params,
  copy: Object.fromEntries(
    Object.entries(params.copy).map(([lang, copy]) => [
      lang,
      { ...copy, hint: '', disclaimer: '', modal_title: '', modal_text: '', hub: '' },
    ]),
  ),
});

/** The page with every language's immersive copy seeded, each in its own language ({@link withImmersiveSeeds}).
 *  Pure. */
export const withImmersiveCopy = (params: LandingParams, game: LandingGame | undefined): LandingParams => {
  const texts = subjectOfferTexts(params);
  return {
    ...params,
    copy: Object.fromEntries(
      Object.entries(params.copy).map(([lang, copy]) => [lang, withImmersiveSeeds(copy, game, texts, lang)]),
    ),
  };
};

/** Whether a scene holds any art. Pure. */
export const sceneHasArt = (scene: LandingScene | undefined): boolean =>
  Boolean(scene?.background_desktop_url) ||
  Boolean(scene?.background_mobile_url) ||
  (scene?.characters.length ?? 0) > 0 ||
  (scene?.symbols.length ?? 0) > 0 ||
  Object.values(scene?.parts ?? {}).some(Boolean);

/** How many of a template's scene layers hold art (backgrounds, characters, symbols, parts). Pure. */
export const sceneLayerCount = (scene: LandingScene): number =>
  (scene.background_desktop_url ? 1 : 0) +
  (scene.background_mobile_url ? 1 : 0) +
  scene.characters.length +
  scene.symbols.length +
  Object.values(scene.parts).filter(Boolean).length;

/** The host page URLs are built with: the primary one, else the first. Pure. */
export const primaryHost = (hosts: readonly LandingHost[]): LandingHost | undefined =>
  hosts.find((h) => h.primary) ?? hosts[0];

/** A marketing offer id short enough for a card ("92e6fb50…"). Pure. */
export const shortMoid = (moid: string): string => (moid.length > 10 ? `${moid.slice(0, 8)}…` : moid);

const escapeHtml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** Keeps a preview from ever leaving its frame: a press on a link or a form submit inside it does nothing, so a
 *  preview never sends real traffic to the brand's site (the frame is also sandboxed to scripts only). */
const INERT_LINKS_SCRIPT =
  '<script>(function(){var stop=function(e){var t=e.target;if(t&&t.closest&&t.closest("a")){e.preventDefault();}};' +
  'document.addEventListener("click",stop,true);document.addEventListener("auxclick",stop,true);' +
  'document.addEventListener("submit",function(e){e.preventDefault();},true);})();</script>';

/** A complete preview document with its links made inert (see {@link INERT_LINKS_SCRIPT}), the script placed first in
 *  its head so it is listening before the page's own. Pure. */
export const withInertLinks = (doc: string): string => {
  const head = /<head(\s[^>]*)?>/i.exec(doc);
  if (head)
    return `${doc.slice(0, head.index + head[0].length)}${INERT_LINKS_SCRIPT}${doc.slice(head.index + head[0].length)}`;
  return `${INERT_LINKS_SCRIPT}${doc}`;
};

/** A live page's `full_html` fragment (a self-contained root with its own style, markup and inline scripts) as the
 *  minimal document a preview frame loads, with its links inert. Pure. */
export const liveFragmentDocument = (html: string, title: string): string =>
  withInertLinks(
    '<!doctype html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1">' +
      `<title>${escapeHtml(title)}</title><style>html,body{margin:0;padding:0;}</style></head><body>${html}</body></html>`,
  );

/** An HTML snippet's text, for showing remote HTML (the legal footer) as inert text: parsed by an inert parser (no
 *  scripts run, nothing loads), its text taken with runs of whitespace collapsed. Browser only; '' elsewhere. */
export const htmlToText = (html: string): string => {
  if (!html.trim() || typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

/* ---------- slice 2: versions, generation, publishing (pure) ---------- */

/** What a Generate can draw and write into a version: the scene art (a template with a scene) or the hero art (a
 *  template with a hero), the copy of every page language, and the lettering (the hero lines and the button drawn as
 *  styled cut-outs, every template). */
export type LandingOp = 'hero' | 'copy' | 'scene' | 'lettering';

export const LANDING_OPS: readonly LandingOp[] = ['scene', 'hero', 'copy', 'lettering'];

/** The price rows of the copy op and of the hero (`landing_hero:<image model>`, the model the kit and mini-game
 *  backgrounds are drawn with). An unpriced row hides the cost (the backend refuses an unpriced op). */
export const LANDING_COPY_PRICE = 'landing_copy';
export const LANDING_HERO_PRICE = 'landing_hero';
/** The scene's price rows: each background `landing_scene_bg:<kit image model>`, each cut-out (a character, the symbol
 *  sheet, a game part) `landing_scene_cutout:<sprite model>`. */
export const LANDING_SCENE_BG_PRICE = 'landing_scene_bg';
export const LANDING_SCENE_CUTOUT_PRICE = 'landing_scene_cutout';

/** A scene's backgrounds (desktop and mobile) and its most characters (the brand's mascots, else two drawn ones). */
export const LANDING_SCENE_BACKGROUNDS = 2;
const LANDING_SCENE_MAX_CHARACTERS = 2;

/** The template parts of a scene's plan: its game parts, and whether it draws the symbol sheet (a full-screen game's
 *  floating symbols; the regular page draws its backgrounds and characters only). */
type SceneTemplate = Pick<LandingTemplate, 'scene_parts' | 'format'>;

/** How many cut-outs a scene draws at most for a template: the characters, a game's symbol sheet and one per game
 *  part. */
export const landingSceneCutouts = (template: SceneTemplate | undefined): number =>
  LANDING_SCENE_MAX_CHARACTERS + (isImmersive(template) ? 1 : 0) + new Set(template?.scene_parts ?? []).size;

/** A row's price on a model (`<prefix>:<model>`), else the one row of that prefix priced. Pure. */
const modelRowCost = (pricing: CreditPricing | null | undefined, prefix: string, model: string): number | undefined => {
  if (model) return creditCostFor(pricing, `${prefix}:${model}`);
  const keys = Object.keys(pricing?.actions ?? {}).filter((k) => k === prefix || k.startsWith(`${prefix}:`));
  return keys.length === 1 ? creditCostFor(pricing, keys[0]) : undefined;
};

/** What a scene run costs: each background and each cut-out (undefined while unpriced), how many of each it draws at
 *  most, and their total (undefined unless both are priced; it may draw one character fewer when the brand has a
 *  single mascot). */
export interface LandingScenePrice {
  background?: number;
  cutout?: number;
  backgrounds: number;
  cutouts: number;
  total?: number;
}

/**
 * The model a scene's backgrounds are drawn with, by the rule spark-creatives routes them (as it routes kit renders):
 * the kit's Flare model when it is served (`flareModel`, the banner-kit catalogue's `flare_model`), the run's art
 * style is not mature (no style counts as not mature) and its `landing_scene_bg` price row exists; otherwise the
 * kit's Seedream model. Pure.
 */
export const landingSceneBgModel = (
  kitModel: string,
  flareModel: string | undefined,
  styleMature: boolean,
  pricing: CreditPricing | null | undefined,
): string =>
  flareModel && !styleMature && modelRowCost(pricing, LANDING_SCENE_BG_PRICE, flareModel) !== undefined
    ? flareModel
    : kitModel;

export const landingScenePrice = (
  pricing: CreditPricing | null | undefined,
  kitModel: string,
  spriteModel: string,
  template: SceneTemplate | undefined,
): LandingScenePrice => {
  const background = modelRowCost(pricing, LANDING_SCENE_BG_PRICE, kitModel);
  const cutout = modelRowCost(pricing, LANDING_SCENE_CUTOUT_PRICE, spriteModel);
  const cutouts = landingSceneCutouts(template);
  return {
    background,
    cutout,
    backgrounds: LANDING_SCENE_BACKGROUNDS,
    cutouts,
    total:
      background !== undefined && cutout !== undefined
        ? background * LANDING_SCENE_BACKGROUNDS + cutout * cutouts
        : undefined,
  };
};

/** The image model a priced registry asset names (`<prefix>:<model>`); '' when no template says. Pure. */
const registryModel = (registry: readonly PlayableTemplate[], prefix: string): string => {
  for (const tpl of registry) {
    for (const asset of tpl.assets) {
      if (asset.price_action.startsWith(`${prefix}:`)) return asset.price_action.slice(prefix.length + 1);
    }
  }
  return '';
};

/** The image model backgrounds are drawn with, read off a playable registry's plate price (`kit_render:<model>`);
 *  '' when no template says. Pure. */
export const kitImageModel = (registry: readonly PlayableTemplate[]): string => registryModel(registry, 'kit_render');

/** The model transparent cut-outs are drawn with, read off a playable registry's sprite price
 *  (`playable_sprite:<model>`); '' when no template says. Pure. */
export const spriteImageModel = (registry: readonly PlayableTemplate[]): string =>
  registryModel(registry, 'playable_sprite');

/** The hero's price row: `landing_hero:<model>` when the model is known, else the one `landing_hero` row priced. */
const heroPriceKey = (pricing: CreditPricing | null | undefined, model: string): string => {
  if (model) return `${LANDING_HERO_PRICE}:${model}`;
  const keys = Object.keys(pricing?.actions ?? {}).filter(
    (k) => k === LANDING_HERO_PRICE || k.startsWith(`${LANDING_HERO_PRICE}:`),
  );
  return keys.length === 1 ? keys[0] : LANDING_HERO_PRICE;
};

/** The ops a Generate can run on this document: the scene art when the template shows a scene, the hero when it
 *  shows one and the owner set no image of their own, and the copy and the lettering on every template. Pure. */
export const landingOpsAvailable = (
  params: LandingParams,
  template?: Pick<LandingTemplate, 'scene' | 'hero'>,
): LandingOp[] =>
  LANDING_OPS.filter((op) => {
    if (op === 'scene') return template?.scene === true;
    if (op === 'hero') return template?.hero === true && params.hero.image_url.trim() === '';
    return true;
  });

/** Whether a scene lacks a layer the template draws: a background, or one of its game parts (a scene drawn for
 *  another game). Pure. */
export const sceneMissesLayers = (
  scene: LandingScene,
  template: Pick<LandingTemplate, 'scene_parts'> | undefined,
): boolean =>
  !scene.background_desktop_url ||
  !scene.background_mobile_url ||
  (template?.scene_parts ?? []).some((key) => !scene.parts[key]);

/** Whether an op starts ticked on a version: a run fills a version once (any landed art op locks it; a copy-only run
 *  leaves its draft open), so what the version
 *  already holds starts unticked (the hero when it has one, generated or the owner's own; the scene when it holds
 *  every layer the template draws) and the copy always starts ticked. The lettering's tick follows spark-creatives'
 *  plan ({@link landingLetteringDefaultOn}); it reads true here. Pure. */
export const landingOpDefaultOn = (
  op: LandingOp,
  params: LandingParams,
  assets: LandingVersionAssets | undefined,
  template?: Pick<LandingTemplate, 'scene_parts'>,
): boolean => {
  if (op === 'hero') return !assets?.hero_url && params.hero.image_url.trim() === '';
  if (op === 'scene') return !sceneHasArt(params.scene) || sceneMissesLayers(params.scene, template);
  return true;
};

/** Whether the lettering starts ticked: with the copy (a rewritten copy needs its lettering drawn again), or when the
 *  service's plan says some lettering is missing or no longer reads the copy. Pure. */
export const landingLetteringDefaultOn = (copyOn: boolean, letteringStale: boolean | undefined): boolean =>
  copyOn || letteringStale === true;

/** The document a run that draws the scene again starts from: the same page with no scene art. Pure. */
export const withoutSceneArt = (params: LandingParams): LandingParams => ({ ...params, scene: emptyScene() });

/* ---------- the scene's plan (spark-creatives' LandingScenePlan and LandingSceneHeldFor) ---------- */

export const LANDING_SCENE_BG_DESKTOP = 'scene:bg_desktop';
export const LANDING_SCENE_BG_MOBILE = 'scene:bg_mobile';
export const LANDING_SCENE_SYMBOLS = 'scene:symbols';
const SCENE_CHARACTER_PREFIX = 'scene:character:';
const SCENE_PART_PREFIX = 'scene:part:';

/** The planned key of the k-th character (1 = left of the game, 2 = right). */
export const landingSceneCharacterKey = (k: number): string => `${SCENE_CHARACTER_PREFIX}${k}`;

/** Whether a planned scene key is a background (priced as one; every other key is a cut-out). Pure. */
export const isSceneBackgroundKey = (key: string): boolean =>
  key === LANDING_SCENE_BG_DESKTOP || key === LANDING_SCENE_BG_MOBILE;

/** How many of a game page's featured games carry http(s), non-SVG key art: the scene draws their own characters and
 *  lifts its floating symbols from that art (spark-creatives `LandingSceneGames`, which also takes any non-SVG url; the
 *  picker only ever stores http(s) art). 0 on any other page. Pure. */
export const landingFeaturedGameArt = (params: Pick<LandingParams, 'featured'>): number => {
  const featured = params.featured;
  if (featured?.type !== 'game') return 0;
  return featured.items
    .slice(0, LANDING_FEATURED_GAMES_MAX)
    .filter((it) => (it.id.trim() || it.name.trim()) && /^https?:\/\//i.test(it.image_url.trim()))
    .filter((it) => !/\.svg$/i.test(it.image_url.trim().replace(/[?#].*$/, ''))).length;
};

/** How many characters a scene run draws: the named cast's (none for no characters); else two on a game page with key
 *  art (`games`, {@link landingFeaturedGameArt}: the game's own character beside the mascot, or the games' characters);
 *  else one per mascot of the art style's cast (`styleCast`: how many of its mascots have an image), two at most, else
 *  two fictional ones. Mirrors spark-creatives `LandingSceneCharacterCount`. Pure. */
export const landingSceneCharacterCount = (cast: readonly string[], styleCast: number, games = 0): number => {
  if (isCastNone(cast)) return 0;
  if (cast.length > 0) return Math.min(cast.length, LANDING_CAST_MAX);
  if (games > 0) return LANDING_CAST_MAX;
  return styleCast > 0 ? Math.min(styleCast, LANDING_CAST_MAX) : LANDING_CAST_MAX;
};

/** Every key a scene run plans for a template, in plan order: both backgrounds, `characters` characters, a full-screen
 *  game's symbol sheet and one per game part (the regular page: the backgrounds and the characters only). Pure. */
export const landingSceneKeys = (template: SceneTemplate | undefined, characters: number): string[] => [
  LANDING_SCENE_BG_DESKTOP,
  LANDING_SCENE_BG_MOBILE,
  ...Array.from({ length: Math.min(Math.max(characters, 0), LANDING_CAST_MAX) }, (_, i) =>
    landingSceneCharacterKey(i + 1),
  ),
  ...(isImmersive(template) ? [LANDING_SCENE_SYMBOLS] : []),
  ...Array.from(new Set((template?.scene_parts ?? []).map((part) => part.trim()).filter(Boolean))).map(
    (part) => `${SCENE_PART_PREFIX}${part}`,
  ),
];

/** Whether a version holds a planned key for the page's cast: drawn into it (`assets.scene_drawn`) and, for a
 *  character under a named cast, drawn as the cast's mascot for its slot (`assets.scene_qa[key].mascot`; a character
 *  drawn before its mascot was recorded is not held). Pure. */
export const landingSceneHeld = (
  key: string,
  cast: readonly string[],
  assets: LandingVersionAssets | undefined,
): boolean => {
  if ((assets?.scene_drawn?.[key]?.length ?? 0) === 0) return false;
  if (!key.startsWith(SCENE_CHARACTER_PREFIX) || cast.length === 0 || isCastNone(cast)) return true;
  const slot = Number(key.slice(SCENE_CHARACTER_PREFIX.length));
  return Number.isInteger(slot) && slot >= 1 && assets?.scene_qa?.[key]?.mascot === cast[slot - 1];
};

/** The page's characters as its version holds them for `cast`, slot by slot (left, right): each planned character
 *  the version holds for the cast ({@link landingSceneHeld}), the rest dropped (a later Scene art run draws them);
 *  none for no characters. A version with no record of its scene keeps `current` under the automatic cast (it cannot
 *  say who was drawn) and holds none for a named one. Pure. */
export const landingCastCharacters = (
  cast: readonly string[],
  assets: LandingVersionAssets | undefined,
  styleCast: number,
  current: readonly string[],
  games = 0,
): string[] => {
  if (isCastNone(cast)) return [];
  if (!assets?.scene_drawn) return cast.length === 0 ? [...current] : [];
  const out: string[] = [];
  for (let k = 1; k <= landingSceneCharacterCount(cast, styleCast, games); k += 1) {
    const key = landingSceneCharacterKey(k);
    const url = assets.scene_drawn[key]?.[0];
    if (url && landingSceneHeld(key, cast, assets)) out.push(url);
  }
  return out;
};

/** What a Scene art run would draw on a version. */
export interface LandingScenePlanView {
  /** Every key the template's scene plans for the page's cast. */
  all: string[];
  /** The keys the version does not hold (all of them without a record). */
  missing: string[];
  /** The version records what its scene runs drew: a run then draws (and charges) only what is missing. */
  recorded: boolean;
  /** A run redraws the whole scene (from a document with no scene and no record): asked for, nothing is missing, or
   *  the version has scene art but no record of it. */
  full: boolean;
  /** The keys the run draws and charges. */
  keys: string[];
  /** Scene art starts ticked: something is missing (without a record: the scene has no art, or lacks a layer). */
  defaultOn: boolean;
}

export const landingScenePlan = (args: {
  params: LandingParams;
  assets: LandingVersionAssets | undefined;
  template: SceneTemplate | undefined;
  /** How many of the art style's mascots have an image (0 with no style or no cast). */
  styleCast: number;
  /** The owner asked for the whole scene again. */
  redrawAll: boolean;
}): LandingScenePlanView => {
  const { params, assets, template } = args;
  const all = landingSceneKeys(
    template,
    landingSceneCharacterCount(params.cast, args.styleCast, landingFeaturedGameArt(params)),
  );
  const recorded = assets?.scene_drawn !== undefined;
  const missing = recorded ? all.filter((key) => !landingSceneHeld(key, params.cast, assets)) : all;
  const hasArt = sceneHasArt(params.scene);
  const full = hasArt && (!recorded || missing.length === 0 || args.redrawAll);
  return {
    all,
    missing,
    recorded,
    full,
    keys: full ? all : missing,
    defaultOn: recorded ? missing.length > 0 : !hasArt || sceneMissesLayers(params.scene, template),
  };
};

/** What drawing `keys` costs at a scene's prices: each background and each cut-out; undefined while a needed row is
 *  unpriced. Pure. */
export const landingSceneKeysCost = (
  price: Pick<LandingScenePrice, 'background' | 'cutout'>,
  keys: readonly string[],
): number | undefined => {
  const backgrounds = keys.filter(isSceneBackgroundKey).length;
  const cutouts = keys.length - backgrounds;
  if ((backgrounds > 0 && price.background === undefined) || (cutouts > 0 && price.cutout === undefined))
    return undefined;
  return backgrounds * (price.background ?? 0) + cutouts * (price.cutout ?? 0);
};

/** Whether a version's hero needs the owner's eye: its generated hero is the one on the page (no own image) and the
 *  vision check did not pass it. Pure. */
export const landingHeroNeedsReview = (version: Pick<LandingVersion, 'params' | 'assets'> | undefined): boolean => {
  const qa = version?.assets.hero_qa;
  return Boolean(
    version?.assets.hero_url && !version.params.hero.image_url && qa && qa.status !== '' && qa.status !== 'passed',
  );
};

/** The credit cost of a Generate running `ops` as the front end can estimate it: the hero and copy rows, and the
 *  scene's backgrounds and cut-outs ({@link landingScenePrice}). Undefined while any op is unpriced (the cost chip then
 *  stays hidden rather than under-quoting), and for the lettering, which only spark-creatives' plan prices
 *  ({@link planLandingRun}: what it draws follows its own staleness rule). The composer shows the plan's total and
 *  falls back to this while the plan cannot be read. Pure. */
export const landingOpsCostFor = (
  ops: readonly LandingOp[],
  pricing: CreditPricing | null | undefined,
  /** The image model the hero and the scene's backgrounds are drawn with ({@link kitImageModel}). */
  model = '',
  /** The scene's: the cut-outs' model ({@link spriteImageModel}), the page's template (its game parts), the
   *  backgrounds' model ({@link landingSceneBgModel}; default `model`) and the keys the run draws
   *  ({@link landingScenePlan}; default the whole scene). */
  scene?: {
    spriteModel: string;
    template: SceneTemplate | undefined;
    bgModel?: string;
    keys?: readonly string[];
  },
): number | undefined => {
  let total = 0;
  for (const op of ops) {
    if (op === 'lettering') return undefined;
    const scenePrice =
      op === 'scene'
        ? landingScenePrice(pricing, scene?.bgModel || model, scene?.spriteModel ?? '', scene?.template)
        : undefined;
    const cost = scenePrice
      ? scene?.keys
        ? landingSceneKeysCost(scenePrice, scene.keys)
        : scenePrice.total
      : creditCostFor(pricing, op === 'hero' ? heroPriceKey(pricing, model) : LANDING_COPY_PRICE);
    if (cost === undefined) return undefined;
    total += cost;
  }
  return total;
};

/** Whether a version holds generated hero art. Pure. */
export const landingHasArt = (assets: LandingVersionAssets | undefined): boolean => Boolean(assets?.hero_url);

/** A version's art without its failure notes. Pure. */
export const landingArtOf = (assets: LandingVersionAssets | undefined): LandingVersionAssets => {
  const copy = JSON.parse(JSON.stringify(assets ?? {})) as LandingVersionAssets;
  delete copy.errors;
  return copy;
};

/** Template ids as the same template: `canon` maps a retired id to its successor ({@link canonicalTemplateId}). */
type TemplateCanon = (id: string) => string;
const sameId: TemplateCanon = (id) => id;

/** What a page features, as one comparable value ('' when the welcome offer leads). Pure. */
const featuredKey = (featured: LandingFeatured | undefined): string =>
  featured ? `${featured.type}:${featured.items.map((item) => `${item.id}|${item.image_url}`).join(',')}` : '';

/** Whether an edit changes what the art is drawn from: the template (a retired id and its successor are the same one,
 *  through `canon`), the promotion, what the page features, or the art note. Copy, offer, links, languages and game
 *  tiles never reach the art (the lettering follows the copy on its own: a fork keeps it, stale where the copy
 *  changed). Pure. */
export const landingArtChanged = (a: LandingParams, b: LandingParams, canon: TemplateCanon = sameId): boolean =>
  canon(a.template_id) !== canon(b.template_id) ||
  a.subject_type !== b.subject_type ||
  a.subject.id !== b.subject.id ||
  a.subject.image_url !== b.subject.image_url ||
  featuredKey(a.featured) !== featuredKey(b.featured) ||
  a.art_direction.trim() !== b.art_direction.trim();

/** Whether a version holds drawn art a later edit could contradict: a generated hero, scene art in its document, or a
 *  record of what its scene runs drew. Pure. */
export const landingHoldsArt = (version: Pick<LandingVersion, 'params' | 'assets'> | undefined): boolean =>
  Boolean(version) &&
  (landingHasArt(version?.assets) ||
    sceneHasArt(version?.params.scene) ||
    Object.keys(version?.assets.scene_drawn ?? {}).length > 0);

/** Whether the next run's art picks differ from what the version's scene art was drawn with: another art style
 *  (`styleId`; undefined while the pick is not known yet, which never counts), or, under the automatic cast, another
 *  lead character (`mascotPick`, the run's `mascot_id`: '' automatic). Such art cannot be filled in around, so the run
 *  draws the whole scene again. False for a version with no scene art. Pure. */
export const landingScenePicksChanged = (
  version: Pick<LandingVersion, 'params' | 'assets' | 'art_style'> | undefined,
  picks: {
    styleId: string | undefined;
    mascotPick: string;
    /** The run's cast (default: the version's own). */
    cast?: readonly string[];
  },
): boolean => {
  if (!version) return false;
  const holdsScene = sceneHasArt(version.params.scene) || Object.keys(version.assets.scene_drawn ?? {}).length > 0;
  if (!holdsScene) return false;
  const drawn = version.art_style;
  if (picks.styleId !== undefined && (drawn?.style_id ?? '') !== picks.styleId) return true;
  return (picks.cast ?? version.params.cast).length === 0 && (drawn?.mascot_pick ?? '') !== picks.mascotPick;
};

export const cloneLandingParams = (p: LandingParams): LandingParams => JSON.parse(JSON.stringify(p)) as LandingParams;

/** The entry that forks `source` for an edit to `next`: it keeps the source's art while the edit does not reach it,
 *  else it starts with none so the next Generate draws art that matches. The lettering lives in the document, so it
 *  is kept either way. Pure. */
export const landingForkEntry = (
  source: LandingVersion,
  next: LandingParams,
  canon: TemplateCanon = sameId,
): LandingVersionEntry => {
  if (landingArtChanged(source.params, next, canon)) return { params: cloneLandingParams(next), assets: {} };
  return {
    params: cloneLandingParams(next),
    assets: landingArtOf(source.assets),
    ...(source.art_style ? { art_style: { ...source.art_style } } : {}),
  };
};

/** The entry a Scene art run that redraws the WHOLE scene targets: `source` forked onto `next` (a document with no
 *  scene, {@link withoutSceneArt}) without the record of what its scene runs drew, so every key is planned again.
 *  Only an explicit full redraw uses it; every other fork keeps the record. Pure. */
export const landingSceneRedrawEntry = (
  source: LandingVersion,
  next: LandingParams,
  canon: TemplateCanon = sameId,
): LandingVersionEntry => {
  const entry = landingForkEntry(source, next, canon);
  const { scene_drawn: _drawn, scene_qa: _qa, ...assets } = entry.assets;
  return { ...entry, assets };
};

/** What a Generate running `ops` would plan against, as the composer's run sets it up: the document it saves (without
 *  its scene when the whole scene is drawn again) and the art of the version it fills. A draft is filled in place; a
 *  generated version is forked (keeping its art while the edit does not reach it); a run that redraws a held hero
 *  fills a version with no art, one that redraws the whole scene a fork without the scene's record. Pure. */
export const landingRunTarget = (args: {
  params: LandingParams;
  version: LandingVersion | undefined;
  ops: readonly LandingOp[];
  /** The scene is drawn again whole ({@link LandingScenePlanView.full}). */
  sceneFull: boolean;
  canon?: TemplateCanon;
}): { params: LandingParams; assets: LandingVersionAssets } => {
  const { version, ops, canon = sameId } = args;
  const redrawsScene = ops.includes('scene') && args.sceneFull;
  const params = redrawsScene ? withoutSceneArt(args.params) : args.params;
  if (!version || (ops.includes('hero') && landingHasArt(version.assets))) return { params, assets: {} };
  if (redrawsScene) return { params, assets: landingSceneRedrawEntry(version, params, canon).assets };
  if (version.locked === true) return { params, assets: landingForkEntry(version, params, canon).assets };
  return { params, assets: landingArtOf(version.assets) };
};

/** The newest generated (locked) version, else the newest: what a card shows. Pure. */
export const landingPosterVersion = (page: LandingPage): LandingVersion | undefined =>
  page.ad_versions.find((v) => v.locked) ?? page.ad_versions[0];

/** The hero a version renders with: the owner's own image, else the generated one ('' when neither). Pure. */
export const landingHeroUrl = (version: Pick<LandingVersion, 'params' | 'assets'> | undefined): string =>
  version ? version.params.hero.image_url || version.assets.hero_url || '' : '';

/** The longest and shortest path a page may be published at (after its slash). */
export const LANDING_PATH_MIN = 2;
export const LANDING_PATH_MAX = 60;

/** Why a path slug cannot be published at, or null when it can: lowercase letters, digits, `-` and `_`, 2 to 60 of
 *  them. Pure. */
export const landingPathProblem = (slug: string): 'empty' | 'short' | 'long' | 'chars' | null => {
  if (slug === '') return 'empty';
  if (!/^[a-z0-9_-]+$/.test(slug)) return 'chars';
  if (slug.length < LANDING_PATH_MIN) return 'short';
  if (slug.length > LANDING_PATH_MAX) return 'long';
  return null;
};

/** A path slug from a page name ("Welcome Wheel!" -> "welcome-wheel"): accents dropped, every other run of characters
 *  a hyphen, capped at the longest path. Pure. */
export const slugFromName = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, LANDING_PATH_MAX)
    .replace(/-+$/g, '');

/** The most words and characters a suggested path holds (a path to read and type, not the whole name). */
export const LANDING_SLUG_WORDS_MAX = 5;
export const LANDING_SLUG_CHARS_MAX = 40;

/** Letters NFD does not take apart, written the way a URL spells them. */
const SLUG_LETTERS: Record<string, string> = { ß: 'ss', æ: 'ae', ø: 'o', œ: 'oe', đ: 'd', ł: 'l', þ: 'th' };

/** Joining words a suggested path never ends on ("welcome-to-the" reads cut off). */
const SLUG_TAIL_WORDS = new Set([
  'a',
  'an',
  'and',
  'or',
  'of',
  'the',
  'to',
  'for',
  'with',
  'in',
  'on',
  'at',
  'by',
  'de',
  'del',
  'la',
  'el',
  'y',
  'e',
  'da',
  'do',
  'des',
  'le',
  'les',
  'et',
]);

/** A token that reads as an id or a hash, not a word: a long number, or a long run mixing letters and digits. */
const isIdToken = (word: string): boolean =>
  /^\d{5,}$/.test(word) || (word.length >= 8 && /\d/.test(word) && /[a-z]/.test(word));

/** The words of a name as a URL spells them: accents folded, lowercase ASCII letters and digits, ids and hashes
 *  dropped ("Bônus 100% até €500" -> bonus, 100, ate, 500). Pure. */
export const slugWords = (text: string): string[] =>
  text
    .toLowerCase()
    .replace(/[ßæøœđłþ]/g, (ch) => SLUG_LETTERS[ch] ?? ch)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '' && !isIdToken(word));

/** Words that fit a suggested path: at most {@link LANDING_SLUG_WORDS_MAX} words and {@link LANDING_SLUG_CHARS_MAX}
 *  characters, cut between words, never ending on a joining word. */
const fitSlugWords = (words: readonly string[]): string[] => {
  const out: string[] = [];
  for (const word of words) {
    if (out.length >= LANDING_SLUG_WORDS_MAX) break;
    if ([...out, word].join('-').length > LANDING_SLUG_CHARS_MAX) {
      if (out.length === 0) out.push(word.slice(0, LANDING_SLUG_CHARS_MAX));
      break;
    }
    out.push(word);
  }
  while (out.length > 1 && SLUG_TAIL_WORDS.has(out[out.length - 1])) out.pop();
  return out;
};

const slugFits = (words: readonly string[]): boolean =>
  words.length <= LANDING_SLUG_WORDS_MAX && words.join('-').length <= LANDING_SLUG_CHARS_MAX;

/** A promotion's words: its name, else its headline; a one-word name reads as an offer ("cashback-offer"). */
const promotionSlugWords = (subject: AdSubject | undefined): string[] => {
  if (!subject) return [];
  const byName = slugWords(subject.name);
  const words = byName.length > 0 ? byName : slugWords(subject.headline ?? '');
  if (words.length !== 1) return words;
  return words[0] === 'offer' ? ['special', 'offer'] : [words[0], 'offer'];
};

/** The featured games' words: one game ("play-starburst" for a one-word name), two that fit ("starburst-and-wolf-
 *  gold"), else the first and more ("starburst-and-more"). */
const gamesSlugWords = (items: readonly AdSubject[]): string[] => {
  const names = items.map((item) => slugWords(item.name || item.headline || '')).filter((w) => w.length > 0);
  if (names.length === 0) return [];
  const [first] = names;
  if (names.length === 1) return first.length === 1 ? ['play', ...first] : first;
  const both = [...first, 'and', ...names[1]];
  if (names.length === 2 && slugFits(both)) return both;
  const more = [...first, 'and', 'more'];
  return slugFits(more) ? more : first;
};

/** The path a page is suggested when it has none, from what it promotes: the featured promotion's name (or the featured
 *  games'), else the welcome promotion's name or headline, else the template ("spin-and-win", "pick-a-prize",
 *  "welcome-offer"). Lowercase ASCII kebab-case, two to five words, at most 40 characters, no ids. Pure. */
export const landingDefaultSlug = (params: Pick<LandingParams, 'featured' | 'subject'>, game?: LandingGame): string => {
  const { featured } = params;
  const sources: string[][] = [
    featured?.type === 'game' ? gamesSlugWords(featured.items) : promotionSlugWords(featured?.items[0]),
    promotionSlugWords(params.subject),
  ];
  for (const words of sources) {
    const fit = fitSlugWords(words);
    if (fit.length >= 2) return fit.join('-');
  }
  return game === 'wheel' ? 'spin-and-win' : game === 'pick' ? 'pick-a-prize' : 'welcome-offer';
};

/** `slug`, or the first of `slug-2`, `slug-3`... that no path in `takenPaths` ("/slug") uses, within the longest
 *  path. Pure. */
export const freeLandingSlug = (slug: string, takenPaths: readonly string[]): string => {
  const taken = new Set(takenPaths.map((p) => p.replace(/^\/+/, '')));
  if (!taken.has(slug)) return slug;
  for (let n = 2; n < 100; n += 1) {
    const suffix = `-${n}`;
    const next = `${slug.slice(0, LANDING_PATH_MAX - suffix.length).replace(/-+$/, '')}${suffix}`;
    if (!taken.has(next)) return next;
  }
  return slug;
};

/** A slug as the owner types it: lowercased, spaces as hyphens, a leading slash dropped. Pure. */
export const cleanSlugInput = (value: string): string => value.toLowerCase().replace(/^\/+/, '').replace(/\s+/g, '-');

/** The page URL on a landing host. Pure. */
export const landingUrl = (lpDomain: string, path: string): string =>
  `https://${lpDomain}${path.startsWith('/') ? path : `/${path}`}`;

/** Whether a live page is the publication of a saved landing page (same public id, address or path on the same
 *  host). Pure. */
export const isPublicationOf = (live: LivePage, publication: LandingPublication | undefined): boolean => {
  if (!publication) return false;
  if (publication.lpid && live.lpid && publication.lpid === live.lpid) return true;
  if (publication.url && live.url && publication.url === live.url) return true;
  return false;
};

/** The document a run reads without the developer controls: the scene's cast automatic (a cast picked with them is
 *  not kept, like their op ticks), so the run's lead character draws the first character. The same object when the
 *  cast already is automatic. Pure. */
export const withAutomaticCast = (params: LandingParams): LandingParams =>
  params.cast.length === 0 ? params : { ...params, cast: [] };

/** The website's languages as a page holds them: the default first, then the others in the site's order, each once;
 *  empty when the site names none. Pure. */
export const siteLanguageList = (languages: readonly string[], defaultLanguage: string): string[] =>
  Array.from(new Set([defaultLanguage, ...languages].map((l) => l.trim()).filter(Boolean)));

/** The page on every language its brand website supports (`languages`, see {@link siteLanguageList}), the one it
 *  opens in first: the owner's `opening_language` while the website has it, else the website's default. A language
 *  the page lacks starts from its OWN language's seed copy ({@link landingSeedCopy}; on a full-screen game, given its
 *  `template`, with the game's copy seeded in that language too), never another language's text (Lumio writes it on
 *  the next Generate); one the website dropped goes with its copy. The same object when the page already matches, or
 *  when the website names no language (nothing to follow). Pure. */
export const withSiteLanguages = (
  params: LandingParams,
  languages: readonly string[],
  defaultLanguage: string,
  template?: Pick<LandingTemplate, 'format' | 'game'>,
): LandingParams => {
  const listed = siteLanguageList(languages, defaultLanguage);
  const opening = params.opening_language.trim();
  const site = opening && listed.includes(opening) ? siteLanguageList(listed, opening) : listed;
  if (site.length === 0) return params;
  const same =
    params.default_language === site[0] &&
    params.languages.length === site.length &&
    params.languages.every((l, i) => l === site[i]);
  if (same) return params;
  const texts = subjectOfferTexts(params);
  const seed = (lang: string): LandingCopy =>
    isImmersive(template)
      ? withImmersiveSeeds(landingSeedCopy(lang), template?.game, texts, lang)
      : landingSeedCopy(lang);
  return {
    ...params,
    languages: site,
    default_language: site[0],
    copy: Object.fromEntries(site.map((lang) => [lang, params.copy[lang] ?? seed(lang)])),
  };
};

/** A new page's document: the welcome promotion it converts to (the welcome bonus, else none) and nothing featured
 *  (the welcome offer leads until the owner picks what the page promotes), every language of the brand's website (the
 *  default first), each with its own language's seed copy to rewrite ({@link landingSeedCopy}; with the reveal, call to
 *  play and win modal seeded from the promotion when the template is a full-screen game), the offer, the template (''
 *  until the owner picks one), the promotion's link, no own hero, no scene, no lettering (Lumio draws them) and no game
 *  tiles (filled automatically). Pure. */
export const newLandingParams = (args: {
  subject: AdSubject;
  ctaUrl: string;
  /** The default language. */
  language: string;
  /** Every language of the brand's website (the default is put first); just the default when omitted. */
  languages?: readonly string[];
  templateId: string;
  offer: LandingOffer;
  /** The template, when known (an immersive one seeds its copy). */
  template?: Pick<LandingTemplate, 'format' | 'game'>;
}): LandingParams => {
  const languages = siteLanguageList(args.languages ?? [], args.language);
  const params: LandingParams = {
    template_id: args.templateId,
    languages,
    default_language: languages[0] ?? args.language,
    opening_language: '',
    copy: Object.fromEntries(languages.map((lang) => [lang, landingSeedCopy(lang)])),
    offer: { ...args.offer },
    cta_url: args.ctaUrl,
    hero: { image_url: '', video_url: '', poster_url: '' },
    payment_icons: [],
    title: '',
    scene: emptyScene(),
    subject_type: 'promotion',
    subject: { ...args.subject },
    art_direction: '',
    typography: { display: '', body: '' },
    cast: [],
    lettering: {},
    games: [],
  };
  return isImmersive(args.template) ? withImmersiveCopy(params, args.template?.game) : params;
};
